// On-chain access: connection, program, PDAs, account decoding, curve/well math.
import { AnchorProvider, Program, BN, EventParser } from "@coral-xyz/anchor";
import { Connection, PublicKey } from "@solana/web3.js";
import { getAssociatedTokenAddressSync, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID } from "@solana/spl-token";
import rawIdl from "./idl.json";
import { CONFIG } from "./config.js";

export { BN, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID };

export const PROGRAM_ID = new PublicKey(CONFIG.programId);
export const METADATA_PROGRAM_ID = new PublicKey("metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s");
export const TOKEN_DECIMALS = 6;
export const TOKEN_UNIT = 1_000_000n;
export const LAMPORTS = 1_000_000_000n;
export const ACC_SCALE = 1_000_000_000_000n;
export const MIN_STAKE = 1_000_000n;
export const DRIP_SECONDS = 3600;
export const MAX_DEV_BUY_BPS = 1500n;
export const BPS = 10_000n;

export const idl = { ...rawIdl, address: PROGRAM_ID.toBase58() };

export const connection = new Connection(CONFIG.rpcUrl, {
  commitment: "confirmed",
  ...(CONFIG.wsUrl ? { wsEndpoint: CONFIG.wsUrl } : {}),
  disableRetryOnRateLimit: false,
});

const readOnlyWallet = {
  publicKey: PublicKey.default,
  signTransaction: async () => {
    throw new Error("Read-only provider");
  },
  signAllTransactions: async () => {
    throw new Error("Read-only provider");
  },
};
export const program = new Program(idl, new AnchorProvider(connection, readOnlyWallet, { commitment: "confirmed" }));
export const eventParser = new EventParser(PROGRAM_ID, program.coder);

// ------------------------------------------------------------------ PDAs
const enc = (s) => new TextEncoder().encode(s);
const pda = (seeds, prog = PROGRAM_ID) => PublicKey.findProgramAddressSync(seeds, prog)[0];

export const configPda = () => pda([enc("config")]);
export const launchPda = (mint) => pda([enc("launch"), mint.toBytes()]);
export const launchIndexPda = (index) => {
  const b = new Uint8Array(8);
  new DataView(b.buffer).setBigUint64(0, BigInt(index), true);
  return pda([enc("launch_index"), b]);
};
export const curvePda = (mint) => pda([enc("curve"), mint.toBytes()]);
export const wellPda = (mint) => pda([enc("well"), mint.toBytes()]);
export const bucketPda = (mint, owner) => pda([enc("bucket"), mint.toBytes(), owner.toBytes()]);
export const metadataPda = (mint) => pda([enc("metadata"), METADATA_PROGRAM_ID.toBytes(), mint.toBytes()], METADATA_PROGRAM_ID);
export const ata = (mint, owner) => getAssociatedTokenAddressSync(mint, owner, true, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID);

// ------------------------------------------------------------------ decoding
const big = (v) => (v === undefined || v === null ? 0n : BigInt(v.toString()));

function decode(name, info) {
  if (!info) return null;
  if (!info.owner.equals(PROGRAM_ID)) return null;
  try {
    return program.coder.accounts.decode(name, info.data);
  } catch {
    return null;
  }
}

export function parseConfig(info) {
  const a = decode("globalConfig", info);
  if (!a) return null;
  return {
    admin: a.admin,
    treasury: a.treasury,
    startFeeBps: a.startFeeBps,
    baseFeeBps: a.baseFeeBps,
    platformFeeBps: a.platformFeeBps,
    decaySeconds: a.decaySeconds,
    initialVirtualSol: big(a.initialVirtualSol),
    initialVirtualTokens: big(a.initialVirtualTokens),
    totalSupply: big(a.totalSupply),
    launchCount: big(a.launchCount),
    launchesPaused: a.launchesPaused,
  };
}
export function parseLaunch(info) {
  const a = decode("launchConfig", info);
  if (!a) return null;
  return {
    mint: a.mint,
    creator: a.creator,
    index: big(a.index),
    launchTimestamp: Number(big(a.launchTimestamp)),
    startFeeBps: a.startFeeBps,
    baseFeeBps: a.baseFeeBps,
    platformFeeBps: a.platformFeeBps,
    decaySeconds: a.decaySeconds,
    devBuyLamports: big(a.devBuyLamports),
    devBuyTokens: big(a.devBuyTokens),
  };
}
export function parseCurve(info) {
  const a = decode("bondingCurveState", info);
  if (!a) return null;
  return {
    virtualSol: big(a.virtualSolReserves),
    virtualTokens: big(a.virtualTokenReserves),
    realSol: big(a.realSolReserves),
    realTokens: big(a.realTokenReserves),
    totalSupply: big(a.totalSupply),
    volume: big(a.volumeLamports),
    buyCount: big(a.buyCount),
    sellCount: big(a.sellCount),
    lastTradeTs: Number(big(a.lastTradeTs)),
  };
}
export function parseWell(info) {
  const a = decode("wellRewardVault", info);
  if (!a) return null;
  const waterTotal = big(a.waterTotal);
  const waterClaimed = big(a.waterClaimed);
  return {
    waterTotal,
    waterClaimed,
    waterReservoir: big(a.waterReservoir),
    totalStaked: big(a.totalStaked),
    acc: big(a.accWaterPerShare),
    drops: big(a.drops),
    lastDropTs: Number(big(a.lastDropTs)),
    lastDripTs: Number(big(a.lastDripTs)),
    level: waterTotal > waterClaimed ? waterTotal - waterClaimed : 0n,
  };
}
export function parseBucket(info) {
  const a = decode("wellBucket", info);
  if (!a) return null;
  return {
    amount: big(a.amount),
    rewardDebt: big(a.rewardDebt),
    unclaimed: big(a.unclaimed),
    totalClaimed: big(a.totalClaimed),
  };
}
export function parseLaunchIndex(info) {
  const a = decode("launchIndex", info);
  return a ? a.mint : null;
}
export function parseTokenAmount(info) {
  // classic SPL token account: amount at offset 64 (u64 LE)
  if (!info || info.data.length < 72) return 0n;
  return new DataView(info.data.buffer, info.data.byteOffset, info.data.byteLength).getBigUint64(64, true);
}

/** Minimal Metaplex Token Metadata decode: name, symbol, uri. */
export function parseMetadata(info) {
  if (!info || !info.owner.equals(METADATA_PROGRAM_ID)) return null;
  const d = info.data;
  const dv = new DataView(d.buffer, d.byteOffset, d.byteLength);
  let o = 1 + 32 + 32;
  const td = new TextDecoder();
  const str = () => {
    const len = dv.getUint32(o, true);
    o += 4;
    const s = td.decode(d.subarray(o, o + len)).replace(/\0+$/g, "").trim();
    o += len;
    return s;
  };
  try {
    return { name: str(), symbol: str(), uri: str() };
  } catch {
    return null;
  }
}

// ------------------------------------------------------------------ fetching
export async function getMultiple(keys) {
  const out = [];
  for (let i = 0; i < keys.length; i += 100) {
    const chunk = keys.slice(i, i + 100);
    const res = await connection.getMultipleAccountsInfo(chunk, "confirmed");
    out.push(...res);
  }
  return out;
}

let configCache = null;
export async function fetchConfig(force = false) {
  if (configCache && !force && Date.now() - configCache.at < 15_000) return configCache.value;
  const info = await connection.getAccountInfo(configPda(), "confirmed");
  const value = parseConfig(info);
  configCache = { at: Date.now(), value };
  return value;
}

/** Load the newest `limit` launches via launch_index PDAs (no getProgramAccounts). */
export async function fetchLaunches(limit = 60) {
  const cfg = await fetchConfig(true);
  if (!cfg) return { config: null, launches: [] };
  const count = Number(cfg.launchCount);
  const idx = [];
  for (let i = count - 1; i >= Math.max(0, count - limit); i--) idx.push(i);
  const idxInfos = await getMultiple(idx.map(launchIndexPda));
  const mints = idxInfos.map(parseLaunchIndex).filter(Boolean);
  const keys = [];
  for (const m of mints) keys.push(launchPda(m), curvePda(m), wellPda(m), metadataPda(m));
  const infos = await getMultiple(keys);
  const launches = mints.map((mint, i) => ({
    mint,
    launch: parseLaunch(infos[i * 4]),
    curve: parseCurve(infos[i * 4 + 1]),
    well: parseWell(infos[i * 4 + 2]),
    meta: parseMetadata(infos[i * 4 + 3]),
  }));
  return { config: cfg, launches: launches.filter((l) => l.launch && l.curve && l.well) };
}

/** Everything the token page needs in one round trip. */
export async function fetchTokenState(mint, owner) {
  const keys = [launchPda(mint), curvePda(mint), wellPda(mint), metadataPda(mint), configPda()];
  if (owner) keys.push(bucketPda(mint, owner), ata(mint, owner), owner);
  const infos = await getMultiple(keys);
  return {
    launch: parseLaunch(infos[0]),
    curve: parseCurve(infos[1]),
    well: parseWell(infos[2]),
    meta: parseMetadata(infos[3]),
    config: parseConfig(infos[4]),
    bucket: owner ? parseBucket(infos[5]) : null,
    tokenBalance: owner ? parseTokenAmount(infos[6]) : 0n,
    ataExists: owner ? !!infos[6] : false,
    solBalance: owner && infos[7] ? BigInt(infos[7].lamports) : 0n,
    // accounts exist on chain but did not decode = created by an older program version
    legacy: !!(infos[0] || infos[1] || infos[2]) && !(parseLaunch(infos[0]) && parseCurve(infos[1]) && parseWell(infos[2])),
  };
}

// off-chain JSON metadata (image, description, links), cached
const jsonCache = new Map();
export function fetchJsonMeta(uri) {
  if (!uri) return Promise.resolve(null);
  if (jsonCache.has(uri)) return jsonCache.get(uri);
  const p = (async () => {
    try {
      const ctl = new AbortController();
      const t = setTimeout(() => ctl.abort(), 8000);
      const r = await fetch(uri, { signal: ctl.signal });
      clearTimeout(t);
      if (!r.ok) return null;
      return await r.json();
    } catch {
      return null;
    }
  })();
  jsonCache.set(uri, p);
  return p;
}

// ------------------------------------------------------------------ websocket probe
// Some RPCs (e.g. the Alchemy devnet endpoint) reject accountSubscribe; web3.js would then retry
// forever. Probe once with a raw socket and only use connection.onAccountChange if it works.
let wsProbe = null;
export function wsAccountSubscribeSupported() {
  if (wsProbe) return wsProbe;
  wsProbe = new Promise((resolve) => {
    let url = CONFIG.wsUrl;
    if (!url) url = CONFIG.rpcUrl.replace(/^http/i, "ws");
    let ws;
    const done = (ok) => {
      clearTimeout(timer);
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      resolve(ok);
    };
    const timer = setTimeout(() => done(false), 6000);
    try {
      ws = new WebSocket(url);
    } catch {
      return done(false);
    }
    ws.onerror = () => done(false);
    ws.onopen = () =>
      ws.send(
        JSON.stringify({
          jsonrpc: "2.0",
          id: 1,
          method: "accountSubscribe",
          params: [configPda().toBase58(), { encoding: "base64", commitment: "confirmed" }],
        })
      );
    ws.onmessage = (m) => {
      try {
        const j = JSON.parse(m.data);
        if (j.id === 1) done(typeof j.result === "number" && !j.error);
      } catch {
        done(false);
      }
    };
  });
  return wsProbe;
}

// ------------------------------------------------------------------ cluster clock
// Offset between the cluster's block time and the local clock, so the fee countdown
// does not depend on a skewed local clock.
let clockOffset = 0;
let clockSyncedAt = 0;
export async function syncClock() {
  if (Date.now() - clockSyncedAt < 30_000) return clockOffset;
  try {
    const slot = await connection.getSlot("confirmed");
    const bt = await connection.getBlockTime(slot);
    if (bt) {
      const off = bt - Date.now() / 1000;
      if (Math.abs(off) < 3 * 3600) clockOffset = off;
      clockSyncedAt = Date.now();
    }
  } catch {
    /* keep previous offset */
  }
  return clockOffset;
}
export const chainNow = () => Date.now() / 1000 + clockOffset;

// ------------------------------------------------------------------ math (mirrors the program exactly)
export function currentFeeBps(launch, nowSec) {
  const start = launch.startFeeBps;
  const base = launch.baseFeeBps;
  const decay = launch.decaySeconds;
  if (start <= base || decay === 0) return base;
  const now = Math.floor(nowSec);
  if (now <= launch.launchTimestamp) return start;
  const elapsed = now - launch.launchTimestamp;
  if (elapsed >= decay) return base;
  const dropped = Math.floor(((start - base) * elapsed) / decay);
  return start - dropped;
}

export function splitFee(amount, feeBps, platformBps) {
  const a = BigInt(amount);
  const total = (a * BigInt(feeBps) + BPS - 1n) / BPS;
  let platform = (a * BigInt(Math.min(platformBps, feeBps))) / BPS;
  if (platform > total) platform = total;
  return { total, platform, water: total - platform };
}

export function quoteBuy(curve, solIn, feeBps, platformBps) {
  const sol = BigInt(solIn);
  if (sol <= 0n) return null;
  const fee = splitFee(sol, feeBps, platformBps);
  const net = sol - fee.total;
  const tokens = (curve.virtualTokens * net) / (curve.virtualSol + net);
  return { solIn: sol, net, tokens, fee, exceeds: tokens > curve.realTokens, zero: tokens === 0n };
}

export function quoteSell(curve, tokensIn, feeBps, platformBps) {
  const t = BigInt(tokensIn);
  if (t <= 0n) return null;
  const gross = (curve.virtualSol * t) / (curve.virtualTokens + t);
  const fee = splitFee(gross, feeBps, platformBps);
  const net = gross - fee.total;
  return { tokensIn: t, gross, net, fee, exceeds: gross > curve.realSol, zero: net <= 0n };
}

/** Quote for a fresh curve built from config (used on the Create page). */
export function freshCurve(cfg) {
  return {
    virtualSol: cfg.initialVirtualSol,
    virtualTokens: cfg.initialVirtualTokens,
    realSol: 0n,
    realTokens: cfg.totalSupply,
    totalSupply: cfg.totalSupply,
  };
}

/** SOL per whole token. */
export function priceSol(curve) {
  if (!curve || curve.virtualTokens === 0n) return 0;
  return Number(curve.virtualSol) / 1e9 / (Number(curve.virtualTokens) / 1e6);
}
export function marketCapSol(curve) {
  return priceSol(curve) * (Number(curve.totalSupply) / 1e6);
}
export function curveProgress(curve) {
  if (!curve || curve.totalSupply === 0n) return 0;
  const sold = curve.totalSupply - curve.realTokens;
  return Number((sold * 1_000_000n) / curve.totalSupply) / 1_000_000;
}

/**
 * Simulate the program's drip(now) on top of the on-chain well state: the reservoir releases
 * reservoir*dt/3600 into the accumulator (everything after >= 1 h), only while someone is staked.
 * `now` may be fractional for a smooth display; the program itself uses whole seconds.
 */
export function previewDrip(well, now = chainNow()) {
  if (!well) return null;
  const out = { acc: well.acc, reservoir: well.waterReservoir, released: 0n };
  if (well.totalStaked === 0n || well.waterReservoir === 0n) return out;
  const dt = now - well.lastDripTs;
  if (!(dt > 0)) return out;
  const release =
    dt >= DRIP_SECONDS
      ? well.waterReservoir
      : (well.waterReservoir * BigInt(Math.floor(dt * 1000))) / BigInt(DRIP_SECONDS * 1000);
  if (release === 0n) return out;
  out.released = release;
  out.reservoir = well.waterReservoir - release;
  out.acc = well.acc + (release * ACC_SCALE) / well.totalStaked;
  return out;
}

/** Claimable water for a bucket, including what the reservoir has dripped since the last transaction. */
export function pendingWater(bucket, well, now = chainNow()) {
  if (!bucket || !well) return 0n;
  const acc = previewDrip(well, now).acc;
  const accrued = (bucket.amount * acc) / ACC_SCALE;
  const pending = accrued > bucket.rewardDebt ? accrued - bucket.rewardDebt : 0n;
  return pending + bucket.unclaimed;
}

/** Largest dev buy (lamports, fee included) whose tokens stay within MAX_DEV_BUY_BPS of supply. */
export function maxDevBuyLamports(cfg) {
  const curve = freshCurve(cfg);
  const cap = (cfg.totalSupply * MAX_DEV_BUY_BPS) / BPS;
  const fits = (lam) => {
    const q = quoteBuy(curve, lam, cfg.baseFeeBps, cfg.platformFeeBps);
    return q && q.tokens <= cap && !q.exceeds;
  };
  let lo = 0n;
  let hi = 1n;
  while (fits(hi) && hi < 10n ** 18n) hi *= 2n;
  while (hi - lo > 1n) {
    const mid = (lo + hi) / 2n;
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  return { lamports: lo, capTokens: cap };
}
/** Decode program events from transaction logs. */
export function parseEvents(logs) {
  const out = [];
  if (!logs) return out;
  try {
    for (const ev of eventParser.parseLogs(logs)) out.push(ev);
  } catch {
    /* malformed logs */
  }
  return out;
}
